// tabs/RecommendationsTab.jsx
// Strategic recommendations tab component showing actionable insights
// Extracted from CompetitiveAnalysisView.jsx for better maintainability

import React from 'react';
import ActionCard from '../shared/ActionCard';
import StrategyCard from '../shared/StrategyCard';
import HelpIcon from '../shared/HelpIcon';

/**
 * Strategic recommendations tab component
 * @param {Object} props - Component props
 * @param {Object} props.analysis - Complete competitive analysis data (dual-source)
 * @returns {JSX.Element} Recommendations tab component
 */
const RecommendationsTab = ({ analysis }) => {
    // Extract from gamalyticsNetwork (new structure) or fallback to old structure
    const gamalyticsData = analysis?.gamalyticsNetwork || analysis || {};
    
    // Safe extraction with defaults
    const recommendations = gamalyticsData?.recommendations || {};
    const immediate = recommendations?.immediate || [];
    const longTerm = recommendations?.longTerm || [];
    const strategic = recommendations?.strategic || [];

    // Sample recommendations if none exist
    const sampleImmediate = immediate.length > 0 ? immediate : [
        {
            action: "Market Research Deep Dive",
            timeframe: "1-2 weeks",
            steps: [
                "Analyze top competitor user reviews for pain points",
                "Identify underserved player segments",
                "Document feature gaps in existing solutions"
            ]
        },
        {
            action: "Competitive Feature Analysis",
            timeframe: "2-3 weeks", 
            steps: [
                "Create detailed feature comparison matrix",
                "Identify unique selling propositions",
                "Plan differentiation strategy"
            ]
        }
    ];

    const sampleLongTerm = longTerm.length > 0 ? longTerm : [
        {
            action: "Market Positioning Strategy",
            timeframe: "3-6 months",
            steps: [
                "Develop unique value proposition",
                "Create go-to-market strategy",
                "Plan marketing and community building approach"
            ]
        }
    ];

    const sampleStrategic = strategic.length > 0 ? strategic : [
        {
            strategy: "Focus on underserved market segments",
            rationale: "Analysis shows gaps in competitor coverage for specific player types",
            risk: "Medium",
            impact: "High revenue potential with lower competition"
        },
        {
            strategy: "Differentiate through innovation",
            rationale: "Competitors are using similar approaches, creating opportunity for disruption",
            risk: "High",
            impact: "Potential market leadership position"
        }
    ];

    return (
        <div className="p-6 space-y-6">
            {/* Explanation banner */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                <div className="text-blue-700 font-medium mb-1">Strategic Recommendations</div>
                <p className="text-blue-600 text-sm">
                    Based on the competitive analysis, here are actionable recommendations to help position your game for success in the market.
                </p>
            </div>

            {/* Immediate Actions */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Immediate Actions
                    <HelpIcon
                        title="Immediate Actions"
                        description="Quick wins and urgent actions you can take right now to improve your competitive position."
                    />
                </h3>
                <div className="space-y-4">
                    {sampleImmediate.map((action, index) => (
                        <ActionCard key={index} action={action} urgent={index < 2} />
                    ))}
                </div>
            </div>

            {/* Long-term Strategy */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Long-term Strategy
                    <HelpIcon
                        title="Long-term Strategy"
                        description="Strategic initiatives that will position you for sustained competitive advantage over time."
                    />
                </h3>
                <div className="space-y-4">
                    {sampleLongTerm.map((action, index) => (
                        <ActionCard key={index} action={action} />
                    ))}
                </div>
            </div>

            {/* Strategic Insights */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Strategic Insights
                    <HelpIcon
                        title="Strategic Insights"
                        description="High-level strategic recommendations based on competitive analysis and market gaps."
                    />
                </h3>
                <div className="space-y-4">
                    {sampleStrategic.map((strategy, index) => (
                        <StrategyCard key={index} strategy={strategy} />
                    ))}
                </div>
            </div>

            {/* Key Takeaways */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Key Takeaways
                    <HelpIcon
                        title="Key Takeaways"
                        description="Essential insights to remember when planning your game development and marketing strategy."
                    />
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                            <div>Focus on areas where competitors show weaknesses or gaps in coverage</div>
                        </div>
                        <div className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                            <div>Consider differentiation through unique features rather than direct competition</div>
                        </div>
                        <div className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                            <div>Monitor competitor strategies continuously as the market evolves</div>
                        </div>
                        <div className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                            <div>Leverage geographic and demographic gaps for targeted market entry</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecommendationsTab;
